import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import {
  Button,
  Drawer,
  Tabs,
  Clickable,
  FormField,
  Icon,
  Feedback,
} from '@fashionnovateam/nova-ui-toolkit';
import { AddTagFormData, ItemFilters, SampleTagResponse } from './types';
import {
  useSampleTagCreate,
  useSampleTagUpdate,
  useSampleTagDetail,
} from '../../../Lib/Hooks/sample-tags.hooks';
import {
  SAMPLE_TYPE_CONFIG,
  FIELD_TO_SECTION_MAP,
  getDefaultFormValues,
  CLASS_AND_LOGISTICS_REQ_DEV_TYPES,
  PROTO_DEVELOPMENT_SECTIONS,
} from './constants';
import {
  ProductImageSection,
  ProductClassificationSection,
  LogisticsSection,
  SampleDetailsSection,
  SelfShellSection,
  CommentsSection,
  POAndProductSection,
  DateFieldsSection,
  CostAndMOQSection,
  ProtoFieldsSection,
  SampleCreatedWithSection,
  AdditionalFabricsSection,
  BlockHeaderSection,
  BlockSampleDetailsSection,
  BlockOtherAttributesSection,
} from './sections';
import {
  handleProtoEditSubmission,
  handleStandardEditSubmission,
  handleCreateSubmission,
} from './utils/submissionHelpers';
import { usePDFDownload } from './hooks/usePDFDownload';
import { useFormInitialization } from './hooks/useFormInitialization';
import { useSampleTypeReset } from './hooks/useSampleTypeReset';

interface AddTagsDrawerProps {
  isAddTagsOpened: boolean;
  setIsAddTagsOpened: (open: boolean) => void;
  domesticMerchPoEligible?: boolean;
  allowedSampleTypes?: ItemFilters[];
  editMode?: boolean;
  editTagId?: string | null;
  onEditComplete?: () => void;
  onCreateNew?: () => void;
  duplicateMode?: boolean;
  duplicateTagId?: string | null;
  onDuplicateComplete?: () => void;
}

const SECTION_CONFIGS: Record<
  string,
  {
    productImage: { disableStyleNumber: boolean };
    sampleCreatedWith: {
      showFabric: boolean;
      showTrim: boolean;
      showLabel: boolean;
      showWashColor: boolean;
    };
  }
> = {
  [ItemFilters.BLOCK]: {
    productImage: { disableStyleNumber: true },
    sampleCreatedWith: {
      showFabric: true,
      showTrim: false,
      showLabel: false,
      showWashColor: false,
    },
  },
  default: {
    productImage: { disableStyleNumber: false },
    sampleCreatedWith: {
      showFabric: true,
      showTrim: true,
      showLabel: true,
      showWashColor: true,
    },
  },
};

interface DrawerState {
  error: string | null;
  isSubmitted: boolean;
  initialFormData: AddTagFormData | null;
  isCreateNewMode: boolean;
  createdTagData: SampleTagResponse | undefined;
  isDownloading: boolean;
  isSubmitting: boolean;
}

function AddTagFormContent({
  activeTab,
  domesticMerchPoEligible,
}: {
  activeTab: ItemFilters;
  domesticMerchPoEligible?: boolean;
}) {
  const { watch } = useFormContext<AddTagFormData>();
  const currentTypeOfDevelopment = watch('typeOfDevelopment');

  const currentConfig = useMemo(() => {
    const baseConfig = SAMPLE_TYPE_CONFIG[activeTab] || { sections: [] };

    if (
      activeTab === ItemFilters.PROTO_DEVELOPMENT &&
      currentTypeOfDevelopment
    ) {
      const needsClassAndLogistics = CLASS_AND_LOGISTICS_REQ_DEV_TYPES.includes(
        currentTypeOfDevelopment,
      );

      return {
        sections: [
          PROTO_DEVELOPMENT_SECTIONS.base[0],
          ...(needsClassAndLogistics
            ? PROTO_DEVELOPMENT_SECTIONS.withClassAndLogistics
            : []),
          ...PROTO_DEVELOPMENT_SECTIONS.base.slice(1),
        ],
      };
    }

    return baseConfig;
  }, [activeTab, currentTypeOfDevelopment]);

  const sectionConfig = SECTION_CONFIGS[activeTab] || SECTION_CONFIGS.default;

  const sectionRenderers: Record<string, () => React.JSX.Element> = useMemo(
    () => ({
      productImage: () => (
        <ProductImageSection {...sectionConfig.productImage} />
      ),
      productClassification: () => <ProductClassificationSection />,
      poAndProduct: () => <POAndProductSection />,
      dateFields: () => <DateFieldsSection />,
      protoFields: () => <ProtoFieldsSection />,
      sampleCreatedWith: () => (
        <SampleCreatedWithSection {...sectionConfig.sampleCreatedWith} />
      ),
      logistics: () => (
        <LogisticsSection domesticMerchPoEligible={domesticMerchPoEligible} />
      ),
      sampleDetails: () => <SampleDetailsSection />,
      selfShell: () => <SelfShellSection />,
      additionalFabrics: () => <AdditionalFabricsSection />,
      costAndMoq: () => <CostAndMOQSection />,
      comments: () => <CommentsSection />,
      blockHeader: () => <BlockHeaderSection />,
      blockSampleDetails: () => <BlockSampleDetailsSection />,
      blockOtherAttributes: () => <BlockOtherAttributesSection />,
    }),
    [domesticMerchPoEligible, sectionConfig],
  );

  return (
    <div className="w-full space-y-6">
      {currentConfig.sections.map((sectionKey: string) => {
        const renderer = sectionRenderers[sectionKey];
        return renderer ? (
          <div key={sectionKey} id={`section-${sectionKey}`}>
            {renderer()}
          </div>
        ) : null;
      })}
    </div>
  );
}

function AddTagsDrawer({
  isAddTagsOpened,
  setIsAddTagsOpened,
  domesticMerchPoEligible = false,
  allowedSampleTypes,
  editMode = false,
  editTagId,
  onEditComplete,
  onCreateNew,
  duplicateMode = false,
  duplicateTagId,
  onDuplicateComplete,
}: AddTagsDrawerProps) {
  const { id: userId } = useParams<{ id: string }>();
  const history = useHistory();
  const drawerContentRef = useRef<HTMLFormElement>(null);

  const [drawerState, setDrawerState] = useState<DrawerState>({
    error: null,
    isSubmitted: false,
    initialFormData: null,
    isCreateNewMode: false,
    createdTagData: undefined,
    isDownloading: false,
    isSubmitting: false,
  });

  const {
    error,
    isSubmitted,
    initialFormData,
    isCreateNewMode,
    createdTagData,
    isDownloading,
    isSubmitting,
  } = drawerState;

  // Data fetching
  const sourceTagId = editTagId || duplicateTagId;
  const shouldFetchTag =
    (editMode && !!editTagId) || (duplicateMode && !!duplicateTagId);

  const { data: sourceTagData, isLoading: isLoadingSourceTag } =
    useSampleTagDetail(userId || '', sourceTagId || '', {
      enabled: shouldFetchTag,
    });

  const defaultSampleType =
    allowedSampleTypes?.[0] || Object.values(ItemFilters)[0];
  const methods = useForm<AddTagFormData>({
    defaultValues: getDefaultFormValues(defaultSampleType),
    mode: 'onSubmit',
    criteriaMode: 'all',
  });

  const { handleSubmit, reset, watch, setValue } = methods;
  const sampleType = watch('sampleType');

  const { downloadPDF } = usePDFDownload(userId);

  useEffect(() => {
    if (
      allowedSampleTypes &&
      allowedSampleTypes.length > 0 &&
      !allowedSampleTypes.includes(sampleType) &&
      !editMode
    ) {
      const correctSampleType = allowedSampleTypes[0];
      reset(getDefaultFormValues(correctSampleType));
    }
  }, [allowedSampleTypes, sampleType, editMode, reset]);

  useFormInitialization({
    isAddTagsOpened,
    editMode,
    duplicateMode,
    isCreateNewMode,
    isSubmitted,
    sourceTagData,
    methods,
    setDrawerState,
  });

  useSampleTypeReset({
    sampleType,
    isAddTagsOpened,
    isSubmitted,
    editMode,
    duplicateMode,
    isCreateNewMode,
    sourceTagData,
    methods,
  });

  // Handlers
  const handleClose = () => {
    setIsAddTagsOpened(false);
    reset(getDefaultFormValues(defaultSampleType));
    setDrawerState({
      error: null,
      isSubmitted: false,
      initialFormData: null,
      isCreateNewMode: false,
      createdTagData: undefined,
      isDownloading: false,
      isSubmitting: false,
    });
  };

  const handleCreateNew = () => {
    handleClose();
    onCreateNew?.();
  };

  const handleDuplicateAndCreateNew = () => {
    const currentFormData = methods.getValues();
    const duplicatedData = {
      ...currentFormData,
      id: undefined,
    };

    if (currentFormData.productImageFile) {
      const newBlobUrl = URL.createObjectURL(currentFormData.productImageFile);
      duplicatedData.productImageUrl = newBlobUrl;
    } else if (currentFormData.productImageUrl) {
      duplicatedData.productImageUrl = currentFormData.productImageUrl;
    }

    setDrawerState((prev) => ({
      ...prev,
      isSubmitted: false,
      isCreateNewMode: true,
      initialFormData: null,
      error: null,
      createdTagData: undefined,
      isSubmitting: false,
    }));
    reset(duplicatedData);
  };

  const scrollToFirstErrorSection = () => {
    setTimeout(() => {
      const formErrors = methods.formState.errors;
      const errorFields = Object.keys(formErrors);

      if (errorFields.length === 0) {
        const drawer = document.querySelector('[data-testid*="tags-drawer"]');
        if (drawer) {
          drawer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      const currentConfig = SAMPLE_TYPE_CONFIG[sampleType] || { sections: [] };

      for (const sectionKey of currentConfig.sections) {
        const sectionId = `section-${sectionKey}`;

        const hasErrorInSection = errorFields.some(
          (field) =>
            FIELD_TO_SECTION_MAP[field] === sectionId ||
            field.startsWith(`${sectionKey}.`) ||
            field.includes('TotalPercentage'),
        );

        if (hasErrorInSection) {
          const sectionElement = document.getElementById(sectionId);
          if (sectionElement) {
            sectionElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
            return;
          }
        }
      }

      const firstError = document.querySelector(
        '.text-red-600, .text-danger-700, [role="alert"]',
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // API mutations
  const { mutate: createTag, isLoading: isCreating } = useSampleTagCreate(
    userId || '',
    {
      onSuccess: (response) => {
        setDrawerState((prev) => ({
          ...prev,
          createdTagData: response,
          isSubmitted: true,
          isSubmitting: false,
        }));
      },
      onError: (err) => {
        setDrawerState((prev) => ({
          ...prev,
          error:
            err instanceof Error ? err.message : 'Failed to create sample tag',
          isSubmitting: false,
        }));
        scrollToFirstErrorSection();
      },
    },
  );

  const { mutate: updateTag, isLoading: isUpdating } = useSampleTagUpdate(
    userId || '',
    {
      onSuccess: (response) => {
        setDrawerState((prev) => ({
          ...prev,
          createdTagData: response,
          isSubmitted: true,
          isSubmitting: false,
        }));
      },
      onError: (err) => {
        setDrawerState((prev) => ({
          ...prev,
          error:
            err instanceof Error ? err.message : 'Failed to update sample tag',
          isSubmitting: false,
        }));
        scrollToFirstErrorSection();
      },
    },
  );

  const onSubmit = async (data: AddTagFormData) => {
    if (!userId) return;
    setDrawerState((prev) => ({ ...prev, error: null, isSubmitting: true }));

    try {
      if (editMode && !isCreateNewMode && sourceTagData && initialFormData) {
        // Proto Development sends entire object, others use change detection
        if (data.sampleType === ItemFilters.PROTO_DEVELOPMENT) {
          await handleProtoEditSubmission(
            data,
            userId,
            sourceTagData,
            updateTag,
          );
        } else {
          await handleStandardEditSubmission(
            data,
            initialFormData,
            userId,
            sourceTagData,
            updateTag,
            handleClose,
          );
        }
      } else {
        await handleCreateSubmission(data, userId, createTag);
      }
    } catch (uploadError) {
      console.error('File upload error during submission:', uploadError);
      setDrawerState((prev) => ({
        ...prev,
        error:
          uploadError instanceof Error
            ? uploadError.message
            : 'Failed to upload image. Please try again.',
        isSubmitting: false,
      }));
    }
  };

  // Loading and computed values
  const isLoading =
    isCreating || isUpdating || isLoadingSourceTag || isSubmitting;
  const drawerTitle =
    editMode && !isCreateNewMode ? 'Edit Sample Tag' : 'Create Sample Tag';

  const getSubmitButtonText = () => {
    if (isLoading) return 'Saving...';
    if (editMode && !isCreateNewMode) return 'Update Sample Tag';
    return 'Create Sample Tag';
  };

  const getTestId = (baseId: string) =>
    editMode && !isCreateNewMode ? `edit-${baseId}` : `add-${baseId}`;

  // Loading state
  if (isLoadingSourceTag) {
    return (
      <Drawer
        isOpen={isAddTagsOpened}
        onClose={handleClose}
        width="720px"
        data-testid={getTestId('tags-drawer')}
      >
        <Drawer.Content className="flex p-6 flex-col items-center justify-center">
          <div className="text-gray-600">Loading...</div>
        </Drawer.Content>
      </Drawer>
    );
  }

  return (
    <Drawer
      isOpen={isAddTagsOpened}
      onClose={handleClose}
      width="720px"
      data-testid={getTestId('tags-drawer')}
    >
      {isSubmitted ? (
        <div className="flex flex-col items-center nu-self-stretch p-10 space-y-6 mt-12">
          <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center">
            <Icon
              name="check/check-tick-circle"
              size="48px"
              color="#5247DF"
              variant="solid"
            />
          </div>
          <h1 className="text-2xl font-semibold text-center">
            {editMode && !isCreateNewMode
              ? `${sampleType} sample tag updated successfully.`
              : `${sampleType} sample tag generated.`}
          </h1>
          <p className="text-base-disabled text-center">
            Download → print → attach to your samples prior to shipping.
          </p>
          <div className="flex space-x-4">
            <Button
              onClick={() => {
                handleClose();
                if (duplicateMode) {
                  onDuplicateComplete?.();
                } else if (editMode && !isCreateNewMode) {
                  onEditComplete?.();
                }
              }}
              color="secondary"
            >
              Back to List
            </Button>
            <Button
              color="primary"
              disabled={isDownloading}
              onClick={async () => {
                try {
                  setDrawerState((prev) => ({
                    ...prev,
                    isDownloading: true,
                    error: null,
                  }));
                  const success = await downloadPDF(createdTagData);
                  if (!success) {
                    setDrawerState((prev) => ({
                      ...prev,
                      error: 'Failed to generate PDF. Please try again.',
                    }));
                  }
                } catch (downloadError) {
                  console.error('Error in downloadPDF:', downloadError);
                  setDrawerState((prev) => ({
                    ...prev,
                    error: 'Failed to generate PDF. Please try again.',
                  }));
                } finally {
                  setDrawerState((prev) => ({ ...prev, isDownloading: false }));
                }
              }}
            >
              {isDownloading ? (
                <>
                  <Icon
                    name="loading-circle"
                    color="white"
                    className="animate-spin mr-1"
                  />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Icon name="download/download-down" color="white" />
                  Download
                </>
              )}
            </Button>
          </div>
          <div className="mt-4 mb-4 border-b nu-border-neutral-200 w-full" />
          <div className="mt-6 text-center">
            <p className="text-base-disabled mt-10 text-md">
              More sample tags to create?
            </p>
            <div className="flex space-x-4 justify-center mt-4">
              <Button color="secondary" onClick={handleDuplicateAndCreateNew}>
                Duplicate & Create New
              </Button>
              <Button color="secondary" onClick={handleCreateNew}>
                Create New
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Drawer.Content className="flex p-6 flex-col items-start self-stretch">
          <Drawer.Title>{drawerTitle}</Drawer.Title>
          <Drawer.Description className="mb-4 mt-1">
            Once submitted, we will generate PDFs you can print and hang on the
            sample.
          </Drawer.Description>

          <FormProvider {...methods}>
            <form
              ref={drawerContentRef}
              onSubmit={handleSubmit(onSubmit, () => {
                scrollToFirstErrorSection();
              })}
              className="w-full flex flex-col gap-5"
              noValidate
            >
              <Feedback
                type="info"
                title="Questions? Get answers."
                description="Visit our help center for frequently asked questions."
              >
                <Button
                  color="secondary"
                  onClick={() => {
                    history.push(`/users/${userId}/help-center/sample%20tags`);
                  }}
                >
                  Help Center
                </Button>
              </Feedback>
              {!(editMode && !isCreateNewMode) && (
                <div
                  id="section-sampleType"
                  className="rounded-xl border border-neutral-200 shadow-sm p-5"
                >
                  <FormField label="Type of Sample Tag" required>
                    <Tabs.Group type="segment" data-testid="sample-tag-tabs">
                      {(allowedSampleTypes || Object.values(ItemFilters)).map(
                        (filter) => (
                          <Clickable
                            key={filter}
                            data-testid={`${filter.toLowerCase().replace(/\s+/g, '-')}-tab-button`}
                            onClick={() => setValue('sampleType', filter)}
                          >
                            <Tabs.Item
                              isActive={sampleType === filter}
                              type="segment"
                              data-testid={`${filter.toLowerCase().replace(/\s+/g, '-')}-tab`}
                            >
                              {filter}
                            </Tabs.Item>
                          </Clickable>
                        ),
                      )}
                    </Tabs.Group>
                  </FormField>
                </div>
              )}

              <AddTagFormContent
                activeTab={sampleType}
                domesticMerchPoEligible={domesticMerchPoEligible}
              />

              {error && (
                <div className="text-red-600 text-sm" role="alert">
                  An error occurred. Please try again.
                </div>
              )}

              <div className="flex gap-3 justify-end w-full pt-4 border-t">
                <Button
                  type="button"
                  color="secondary"
                  onClick={handleClose}
                  data-testid={getTestId('cancel-tags-button')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid={getTestId('submit-sample-tags-button')}
                  disabled={isLoading}
                >
                  {getSubmitButtonText()}
                </Button>
              </div>
            </form>
          </FormProvider>
        </Drawer.Content>
      )}
    </Drawer>
  );
}

export default AddTagsDrawer;
